import { Form } from '@calendly/form';
import { object, string } from '@calendly/form/validation';
import { I18n } from '@calendly/i18n';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useMemo, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAddContactMutation, useEditContactMutation } from 'features/contacts/api';
import { activeContactDrawerSelector, isContactDrawerOpenSelector, getCurrentContact } from 'features/contacts/selectors';
import { ContactFormFields } from 'features/contacts/shared/contact_form_fields';
import { toggleContactDrawerOpen, setSelectedContactDrawer, setCurrentContact, setCurrentContactProfile } from 'features/contacts/slice';
import { useTrack } from 'hooks/analytics_events';
import { trackContactUpdatedEvent } from '../analytics';
import { getActualContactAttributesView } from '../utils';
const newContactContainerIsClosingCss = "n1bkiiml";
const newContactContainerIsPhoneCss = "n1pvhgan";
const newContactContainerCss = "nuzwkke";
const NewContactContainer = forwardRef((props, ref) => {
  const {
    isClosing
  } = props;
  const isPhone = useDeviceType('isPhone');
  return <FixedContainer ref={ref} {...props} className={classNames(newContactContainerCss, {
    [newContactContainerIsClosingCss]: isClosing,
    [newContactContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const headerContainerCss = "h4bwjlw";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});
const styledFormCss = "s1dwqvjk";
const StyledForm = forwardRef((props, ref) => <Form ref={ref} {...props} className={classNames(styledFormCss, props.className)} />);
export const ContactForm = () => {
  const track = useTrack();
  const dispatch = useDispatch();
  const [addContact, {
    error
  }] = useAddContactMutation();
  const [editContact] = useEditContactMutation();
  const isOpen = useSelector(isContactDrawerOpenSelector);
  const selectedDrawer = useSelector(activeContactDrawerSelector);
  const currentContact = getActualContactAttributesView(useSelector(getCurrentContact));
  const showNewContactForm = isOpen && selectedDrawer === 'NEW_CONTACT_FORM';
  const showEditContactForm = isOpen && selectedDrawer === 'EDIT_CONTACT_FORM';
  const initialValues = {
    name: currentContact && currentContact.name ? currentContact.name : '',
    email: currentContact && currentContact.email ? currentContact.email : '',
    notes: currentContact && currentContact.notes ? currentContact.notes : '',
    phone: currentContact && currentContact.phone ? currentContact.phone : '',
    time_zone: currentContact && currentContact.timeZone ? currentContact.timeZone : ''
  };
  const validationSchema = useMemo(() => object({
    email: string().email().required(I18n.t('publisher.contacts.validation.email_required')),
    name: string().required(I18n.t('publisher.contacts.validation.name_required')),
    phone: string().phoneNumber()
  }), []);
  const closeContactDrawer = () => {
    dispatch(toggleContactDrawerOpen());
    dispatch(setSelectedContactDrawer(''));
    dispatch(setCurrentContact(null));
  };
  const handleSubmit = async (values, {
    setFieldError
  }) => {
    if (!values.name.trim()) {
      setFieldError('name', I18n.t('publisher.contacts.validation.name_required'));
      return;
    }
    const contactFields = {
      contact: {
        name: values.name,
        email: values.email,
        phone: values.phone,
        notes: values.notes,
        time_zone: values.time_zone
      }
    };
    if (showNewContactForm) {
      const response = await addContact(contactFields).unwrap();
      const eventTrackingData = {
        contactUuid: response.uuid,
        contactSource: 'contact_list',
        updateType: 'create'
      };
      trackContactUpdatedEvent(eventTrackingData, track);
      dispatch(setCurrentContactProfile(response));
    }
    if (showEditContactForm) {
      const editContactFields = {
        contact: {
          ...contactFields.contact,
          uuid: currentContact.uuid
        }
      };
      editContact(editContactFields);
    }
  };
  if (!showNewContactForm && !showEditContactForm) return null;
  return <NewContactContainer isClosing={!showNewContactForm && !showEditContactForm}>
      <HeaderContainer>
        <IconButton size="small" title="Close" onClick={closeContactDrawer}>
          <CloseIcon />
        </IconButton>
      </HeaderContainer>
      <StyledForm initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} externalError={error}>
        <ContactFormFields handleClose={closeContactDrawer} showEditContactForm={showEditContactForm} />
      </StyledForm>
    </NewContactContainer>;
};

require("./contact_form.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./contact_form.js");